import {
    VStack,
    HStack,
    SelectChangeEvent,
    GroupBase,
    classnames,
    Button,
    Select,
    InlineBanner,
    FormControl,
    PopoverContent,
    Box,
} from '@cad-arc';
import { UpdateToEventEntity, UpdateToEventView } from '@mark43/cad-api';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Controller, ControllerRenderProps, SubmitHandler, useForm } from 'react-hook-form';
import { EntitiesToUpdate, EventAutocomplete, EventOption, StylesOverride } from './types';
import { SelectInstance } from 'react-select';
import { isNil } from 'lodash';

type Option = {
    label: string;
    value: number;
};

type FieldValues = {
    event: EventOption;
    person: number;
    vehicle: number;
};

export const ADD_NEW_ENTITY_ID_TOKEN = -1 as const;

const getOptionsFromEntityArray = (
    entityName: string,
    entityArray?: UpdateToEventEntity[]
): Option[] => {
    if (!entityArray) {
        return [];
    }

    return [
        ...entityArray.reduce((output: Option[], { entityId: value, title: label }) => {
            if (label !== undefined && value !== undefined) {
                output.push({
                    label,
                    value,
                });
            }
            return output;
        }, []),
        {
            label: `Add New ${entityName}`,
            value: ADD_NEW_ENTITY_ID_TOKEN,
        },
    ];
};

const getEntityFromOption = (entityArray: UpdateToEventEntity[], entityId: number) => {
    if (entityId === ADD_NEW_ENTITY_ID_TOKEN) {
        return null;
    }
    return entityArray.find((entity) => entity.entityId === entityId);
};

export const UpdateToEventPopoverContent = ({
    closePopover,
    openConfirmModal,
    updateToEventView,
    setEventId,
    isBootstrapLoading,
    EventAutocompleteComponent,
    stylesOverride,
}: {
    closePopover: () => void;
    openConfirmModal: (selectedEntities: EntitiesToUpdate) => void;
    updateToEventView?: UpdateToEventView;
    setEventId: (eventId?: number) => void;
    isBootstrapLoading: boolean;
    EventAutocompleteComponent?: EventAutocomplete;
    stylesOverride: StylesOverride;
}): React.ReactElement => {
    const {
        clearErrors,
        control,
        formState: { errors },
        handleSubmit,
        resetField,
        setError,
        watch,
    } = useForm<FieldValues>({ mode: 'onSubmit' });

    const isEventSelectorShown = !!EventAutocompleteComponent;

    const watchedEventField = watch('event');

    const isVehicleEnabled = useMemo(
        () =>
            (!isEventSelectorShown || watchedEventField) &&
            updateToEventView?.parsedVehicle !== undefined,
        [isEventSelectorShown, updateToEventView?.parsedVehicle, watchedEventField]
    );
    const isPersonEnabled = useMemo(
        () =>
            (!isEventSelectorShown || watchedEventField) &&
            updateToEventView?.parsedPerson !== undefined,
        [isEventSelectorShown, updateToEventView?.parsedPerson, watchedEventField]
    );

    const [showErrorBanner, setShowErrorBanner] = useState<boolean>();

    const clearFormErrors = useCallback(() => {
        setShowErrorBanner(false);
        clearErrors();
    }, [clearErrors]);

    const personSelectRef = useRef<SelectInstance<unknown, false, GroupBase<unknown>>>();
    const vehicleSelectRef = useRef<SelectInstance<unknown, false, GroupBase<unknown>>>();
    const eventSearchSelectRef = useRef<SelectInstance<unknown, false, GroupBase<unknown>>>();

    const clearPersonAndVehicleFields = useCallback(() => {
        resetField('person');
        resetField('vehicle');
        personSelectRef.current?.clearValue();
        vehicleSelectRef.current?.clearValue();
    }, [resetField]);

    const validateFormWithEvent = useCallback(
        ({ person, vehicle, event }: FieldValues) => {
            if (updateToEventView === undefined) {
                setError('event', {
                    type: 'manual',
                });
                setError('person', {
                    type: 'manual',
                });
                setError('vehicle', {
                    type: 'manual',
                });
                setShowErrorBanner(true);
                return false;
            }

            if (isVehicleEnabled && isPersonEnabled) {
                // isNil is required to support the version of react-hook-form in cad-common as well as cad-next
                // (the fields are null in cad-next and undefined in cad-common)
                if (isNil(person) && isNil(vehicle)) {
                    setError('person', {
                        type: 'manual',
                    });
                    setError('vehicle', {
                        type: 'manual',
                    });
                }
                if (isNil(event)) {
                    setError('event', {
                        type: 'manual',
                    });
                }

                if ((isNil(person) && isNil(vehicle)) || isNil(event)) {
                    setShowErrorBanner(true);
                    return false;
                }
                return true;
            }

            if (isVehicleEnabled || isPersonEnabled) {
                if (isNil(event)) {
                    setError('event', {
                        type: 'manual',
                    });
                }
                if (isPersonEnabled) {
                    if (isNil(person)) {
                        setError('person', {
                            type: 'manual',
                        });
                    }
                } else if (isVehicleEnabled) {
                    if (isNil(vehicle)) {
                        setError('vehicle', {
                            type: 'manual',
                        });
                    }
                }

                if (
                    (isPersonEnabled && isNil(person)) ||
                    (isVehicleEnabled && isNil(vehicle)) ||
                    isNil(event)
                ) {
                    setShowErrorBanner(true);
                    return false;
                } else {
                    return true;
                }
            }
        },
        [isPersonEnabled, isVehicleEnabled, setError, updateToEventView]
    );

    const validateFormWithoutEvent = useCallback(
        ({ person, vehicle }: FieldValues) => {
            if (updateToEventView === undefined) {
                setError('person', {
                    type: 'manual',
                });
                setError('vehicle', {
                    type: 'manual',
                });
                setShowErrorBanner(true);
                return false;
            }

            if (isVehicleEnabled && isPersonEnabled) {
                if (isNil(person) && isNil(vehicle)) {
                    setError('person', {
                        type: 'manual',
                    });
                    setError('vehicle', {
                        type: 'manual',
                    });
                    setShowErrorBanner(true);
                    return false;
                }
                return true;
            }

            if (isVehicleEnabled || isPersonEnabled) {
                if (isPersonEnabled) {
                    if (isNil(person)) {
                        setError('person', {
                            type: 'manual',
                        });
                    }
                } else if (isVehicleEnabled) {
                    if (isNil(vehicle)) {
                        setError('vehicle', {
                            type: 'manual',
                        });
                    }
                }

                if ((isPersonEnabled && isNil(person)) || (isVehicleEnabled && isNil(vehicle))) {
                    setShowErrorBanner(true);
                    return false;
                } else {
                    return true;
                }
            }
        },
        [isPersonEnabled, isVehicleEnabled, setError, updateToEventView]
    );

    const onSubmit: SubmitHandler<FieldValues> = useCallback(
        (fieldValues: FieldValues) => {
            if (isEventSelectorShown) {
                if (!validateFormWithEvent(fieldValues)) {
                    return;
                }
            }
            if (!isEventSelectorShown) {
                if (!validateFormWithoutEvent(fieldValues)) {
                    return;
                }
            }
            if (updateToEventView === undefined) {
                // needed to typeguard the usage of updateToEventView below
                return;
            }
            const entitiesToUpdate = {
                person:
                    fieldValues.person !== undefined
                        ? getEntityFromOption(updateToEventView.eventPersons, fieldValues.person)
                        : undefined,
                vehicle:
                    fieldValues.vehicle !== undefined
                        ? getEntityFromOption(updateToEventView.eventVehicles, fieldValues.vehicle)
                        : undefined,
            };

            openConfirmModal(entitiesToUpdate);
        },
        [
            isEventSelectorShown,
            openConfirmModal,
            updateToEventView,
            validateFormWithEvent,
            validateFormWithoutEvent,
        ]
    );

    const personOptions = useMemo(
        () => getOptionsFromEntityArray('Person', updateToEventView?.eventPersons),
        [updateToEventView?.eventPersons]
    );
    const vehicleOptions = useMemo(
        () => getOptionsFromEntityArray('Vehicle', updateToEventView?.eventVehicles),
        [updateToEventView?.eventVehicles]
    );

    const onSelectChange = useCallback(
        <FieldType extends 'vehicle' | 'person'>(
            selectChangeEvent: SelectChangeEvent<Option, false>,
            field: ControllerRenderProps<FieldValues, FieldType>
        ) => {
            clearFormErrors();
            const selectedOption = {
                ...selectChangeEvent?.detail?.selectedOptionsData?.[0],
            };
            field.onChange(selectedOption?.value ?? null);
        },
        [clearFormErrors]
    );

    return (
        <PopoverContent
            align="end"
            onEscapeKeyDown={closePopover}
            onInteractOutside={closePopover}
            onClick={(event) => {
                event.stopPropagation();
            }}
            className={classnames('ignore-react-onclickoutside', stylesOverride['popover-content'])}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <VStack width="17em">
                    {showErrorBanner ? (
                        <Box Box paddingBottom="var(--arc-space-1)">
                            <InlineBanner
                                description={
                                    isEventSelectorShown
                                        ? 'Select an event and at least one entity to update.'
                                        : 'Select at least one entity to update.'
                                }
                                status="error"
                            />
                        </Box>
                    ) : undefined}
                    {isEventSelectorShown && (
                        <FormControl isInvalid={errors['event'] !== undefined} label="Event">
                            <Controller
                                control={control}
                                name="event"
                                render={({ field }) => (
                                    // eslint-disable-next-line react/forbid-elements
                                    <EventAutocompleteComponent
                                        {...field}
                                        data-testid="event-search"
                                        includeClearedEvents={false}
                                        initialInputValue={updateToEventView?.eventNumber}
                                        onChange={(selectedEvent) => {
                                            clearFormErrors();
                                            clearPersonAndVehicleFields();
                                            field.onChange(selectedEvent ?? null);
                                            setEventId(selectedEvent?.data.agencyEventId);
                                        }}
                                        selectRef={eventSearchSelectRef}
                                    />
                                )}
                            />
                        </FormControl>
                    )}
                    <Box paddingTop="var(--arc-space-1)" width="100%">
                        <FormControl
                            isDisabled={!isPersonEnabled || isBootstrapLoading}
                            isInvalid={errors['person'] !== undefined}
                            label="Person"
                            className={stylesOverride['form-control']}
                        >
                            <Controller
                                name="person"
                                control={control}
                                render={({ field }) => (
                                    // eslint-disable-next-line react/forbid-elements
                                    <Select
                                        {...field}
                                        data-testid="person-select"
                                        isDisabled={!isPersonEnabled || isBootstrapLoading}
                                        onChange={(selectChangeEvent) => {
                                            onSelectChange<'person'>(selectChangeEvent, field);
                                        }}
                                        options={personOptions}
                                        ref={personSelectRef as any}
                                        emptyState="No persons found on event"
                                        className={stylesOverride['select']}
                                    />
                                )}
                            />
                        </FormControl>
                    </Box>
                    <Box paddingTop="var(--arc-space-1)" width="100%">
                        <FormControl
                            isDisabled={!isVehicleEnabled || isBootstrapLoading}
                            isInvalid={errors['vehicle'] !== undefined}
                            label="Vehicle"
                            className={stylesOverride['form-control']}
                        >
                            <Controller
                                name="vehicle"
                                control={control}
                                render={({ field }) => (
                                    // eslint-disable-next-line react/forbid-elements
                                    <Select
                                        {...field}
                                        data-testid="vehicle-select"
                                        isDisabled={!isVehicleEnabled || isBootstrapLoading}
                                        onChange={(selectChangeEvent) => {
                                            onSelectChange<'vehicle'>(selectChangeEvent, field);
                                        }}
                                        options={vehicleOptions}
                                        ref={vehicleSelectRef as any}
                                        emptyState="No vehicles found on event"
                                        className={stylesOverride['select']}
                                    />
                                )}
                            />
                        </FormControl>
                    </Box>
                    <Box className={stylesOverride['form-control']} width="100%">
                        <HStack
                            justifyContent="space-between"
                            width="100%"
                            paddingTop="var(--arc-space-2)"
                            className={stylesOverride['button-group']}
                        >
                            <Button
                                variant="ghost"
                                onClick={closePopover}
                                className={stylesOverride['button-ghost']}
                            >
                                Cancel
                            </Button>
                            <Button
                                data-testid="popover-update-button"
                                type="submit"
                                variant="solid"
                                isDisabled={isBootstrapLoading}
                                className={stylesOverride['button-solid']}
                            >
                                Update
                            </Button>
                        </HStack>
                    </Box>
                </VStack>
            </form>
        </PopoverContent>
    );
};
